import { InputAffinity, } from "./input-stream";
/** One canvas-wide OS input bridge shared by every editable Scene node. */
export class NativeTextInputBridge {
    #canvas;
    #dispatch;
    #editContext;
    #onSubmit;
    #onCopy;
    #onPaste;
    #onError;
    #requestCharacterBounds;
    #proxy;
    #removeListeners = [];
    mode;
    #appliedRevision = 0n;
    #composing = false;
    #disposed = false;
    #geometry;
    #pendingCharacterBounds;
    #selection = { anchor: 0, focus: 0 };
    #sentRevision = 0n;
    #syncingSurface = false;
    #target;
    #value = "";
    constructor(canvas, options) {
        this.#canvas = canvas;
        this.#dispatch = options.dispatch;
        this.#onSubmit = options.onSubmit;
        this.#onCopy = options.onCopy;
        this.#onPaste = options.onPaste;
        this.#onError = options.onError;
        this.#requestCharacterBounds = options.requestCharacterBounds;
        const ownerDocument = options.ownerDocument ?? canvas.ownerDocument;
        const constructor = options.editContext === undefined
            ? Reflect.get(ownerDocument.defaultView ?? globalThis, "EditContext")
            : (options.editContext ?? undefined);
        if (constructor !== undefined) {
            this.mode = "edit-context";
            this.#editContext = new constructor({ text: "", selectionStart: 0, selectionEnd: 0 });
            Reflect.set(canvas, "editContext", this.#editContext);
            this.listen(this.#editContext, "textupdate", this.handleTextUpdate);
            this.listen(this.#editContext, "compositionstart", this.handleCompositionStart);
            this.listen(this.#editContext, "compositionend", this.handleCompositionEnd);
            this.listen(this.#editContext, "characterboundsupdate", this.handleCharacterBoundsUpdate);
            this.listen(canvas, "keydown", this.handleKeyDown);
            // EditContext only replaces text input: the built-in undo stack is
            // disabled outright and the clipboard stays the application's job. The
            // listeners sit on the document, not the canvas — events targeted at the
            // host bubble up here anyway, and a browser that routes them at the
            // document because it does not treat the host as editable is still
            // caught. The handlers no-op while no editor is active.
            const clipboardHost = typeof ownerDocument.addEventListener === "function" ? ownerDocument : canvas;
            this.listen(clipboardHost, "copy", this.handleCopy);
            this.listen(clipboardHost, "cut", this.handleCut);
            this.listen(clipboardHost, "paste", this.handlePaste);
        }
        else {
            this.mode = "textarea-proxy";
            this.#proxy = createProxy(ownerDocument);
            this.listen(this.#proxy, "beforeinput", this.handleBeforeInput);
            this.listen(this.#proxy, "compositionstart", this.handleCompositionStart);
            this.listen(this.#proxy, "compositionupdate", this.handleCompositionUpdate);
            this.listen(this.#proxy, "compositionend", this.handleCompositionEnd);
            this.listen(this.#proxy, "select", this.handleProxySelection);
            this.listen(this.#proxy, "copy", this.handleCopy);
            this.listen(this.#proxy, "cut", this.handleCut);
            this.listen(this.#proxy, "paste", this.handlePaste);
        }
    }
    get activeNodeId() {
        return this.#target?.nodeId;
    }
    /**
     * Whether a DOM node belongs to the surface this bridge owns.
     *
     * The host ends a session when a press lands outside the editor, and the
     * proxy lives in `document.body` rather than inside the canvas, so a press on
     * it would otherwise read as a press somewhere else on the page.
     */
    /**
     * Dictionary word boundaries for the value this bridge currently mirrors.
     *
     * UAX #29 has no dictionary, so Core alone makes every Han ideograph its own
     * word and a double click selects one character. `Intl.Segmenter` does have
     * one, and ICU picks it by script rather than by locale, so the host locale
     * is the right one to ask with. Returns `undefined` when the platform has no
     * segmenter or nothing is being edited, and Core keeps its own segmentation.
     */
    wordBoundaries() {
        if (this.#target === undefined || typeof Intl.Segmenter !== "function")
            return undefined;
        const segmenter = new Intl.Segmenter(undefined, { granularity: "word" });
        const offsets = [];
        let utf16 = 0;
        for (const segment of segmenter.segment(this.#value)) {
            offsets.push(utf16);
            utf16 += segment.segment.length;
        }
        // Offsets index the value this bridge mirrors, so they are only valid for
        // the revision it has applied; Core drops them when that has moved on.
        return { baseRevision: this.#appliedRevision, offsets };
    }
    ownsNode(node) {
        if (node === null || this.#proxy === undefined)
            return false;
        return this.#proxy === node || this.#proxy.contains(node);
    }
    activate(target) {
        this.assertUsable();
        validateTarget(target);
        // While the platform is composing, the surface is the platform's. A Shell
        // re-states the focused block after every reverse batch -- which is how an
        // input method is kept in step -- and during a composition that arrives
        // mid-word. Tearing the surface down and putting it back lost the
        // composition here while the Core still held it, so the next candidate
        // began a second one on top of the first and the frame was rejected.
        if (this.#composing && this.#target?.nodeId === target.nodeId)
            return;
        this.#target = { ...target, selection: { ...target.selection } };
        this.#value = target.value;
        this.#selection = { ...target.selection };
        this.#appliedRevision = target.revision;
        this.#sentRevision = target.revision;
        this.#composing = false;
        this.#pendingCharacterBounds = undefined;
        this.syncSurface();
        this.applyInputMode(target.inputMode ?? "text");
        this.attachEditContext(true);
        this.#canvas.focus({ preventScroll: true });
        this.#proxy?.focus({ preventScroll: true });
    }
    deactivate() {
        this.#target = undefined;
        this.#composing = false;
        this.applyInputMode("none");
        // Detached, not just logically ended. An EditContext left on a focused
        // element keeps the OS text service engaged: the soft keyboard stays up and
        // the IME stays armed on a field the user has clicked away from. Pressing
        // outside the canvas hides that, because the browser moves focus off the
        // canvas itself; pressing elsewhere inside it does not, which is what made
        // the two look like different features.
        this.attachEditContext(false);
        this.#proxy?.blur();
    }
    attachEditContext(attached) {
        if (this.#editContext === undefined)
            return;
        Reflect.set(this.#canvas, "editContext", attached ? this.#editContext : null);
    }
    /** Forwards the soft-keyboard hint to whichever surface owns OS input. */
    applyInputMode(inputMode) {
        if (this.#proxy !== undefined) {
            this.#proxy.inputMode = inputMode;
            return;
        }
        if (typeof this.#canvas.setAttribute === "function") {
            this.#canvas.setAttribute("inputmode", inputMode);
        }
    }
    applyTransaction(transaction) {
        this.assertUsable();
        const target = this.#target;
        if (target === undefined || transaction.nodeId !== target.nodeId)
            return;
        if (transaction.baseRevision !== this.#appliedRevision) {
            throw new Error("edit transaction is out of order for the native input bridge");
        }
        if (transaction.revision > this.#sentRevision) {
            // Core-initiated transitions (caret placement, corrections) fast-forward
            // the optimistic counter so later local input uses the right base.
            this.#sentRevision = transaction.revision;
        }
        if (transaction.delta !== undefined) {
            this.#value = applyUtf16Replacement(this.#value, transaction.delta.range.start, transaction.delta.range.end, transaction.delta.text);
        }
        this.#selection = {
            anchor: transaction.selection.anchor,
            focus: transaction.selection.focus,
        };
        this.#appliedRevision = transaction.revision;
        this.#composing = transaction.composition !== undefined;
        this.syncSurface();
    }
    updateGeometry(geometry) {
        this.assertUsable();
        this.#geometry = geometry;
        this.#editContext?.updateControlBounds(geometry.controlBounds);
        this.#editContext?.updateSelectionBounds(geometry.selectionBounds);
        this.fulfillCharacterBoundsRequest();
    }
    dispose() {
        if (this.#disposed)
            return;
        this.#disposed = true;
        for (const remove of this.#removeListeners.splice(0))
            remove();
        this.#proxy?.remove();
        if (this.#editContext !== undefined)
            Reflect.set(this.#canvas, "editContext", null);
        this.#target = undefined;
    }
    emit(command) {
        const target = this.#target;
        if (target === undefined || target.readOnly)
            return;
        const baseRevision = this.#sentRevision;
        this.#sentRevision += 1n;
        try {
            this.#dispatch({ ...command, nodeId: target.nodeId, baseRevision });
        }
        catch (cause) {
            this.#sentRevision = baseRevision;
            throw cause;
        }
    }
    emitSelection(anchor, focus) {
        const target = this.#target;
        if (target === undefined)
            return;
        const baseRevision = this.#sentRevision;
        this.#sentRevision += 1n;
        const selection = {
            anchor: { offset: anchor, affinity: InputAffinity.Downstream },
            focus: { offset: focus, affinity: InputAffinity.Downstream },
        };
        try {
            this.#dispatch({ type: "setSelection", nodeId: target.nodeId, baseRevision, selection });
        }
        catch (cause) {
            this.#sentRevision = baseRevision;
            throw cause;
        }
    }
    syncSurface() {
        this.#syncingSurface = true;
        try {
            const start = Math.min(this.#selection.anchor, this.#selection.focus);
            const end = Math.max(this.#selection.anchor, this.#selection.focus);
            if (this.#editContext !== undefined) {
                if (this.#editContext.text !== this.#value) {
                    this.#editContext.updateText?.(0, this.#editContext.text.length, this.#value);
                }
                this.#editContext.updateSelection(start, end);
            }
            if (this.#proxy !== undefined) {
                this.#proxy.value = this.#value;
                this.#proxy.readOnly = this.#target?.readOnly ?? true;
                this.#proxy.setSelectionRange(start, end);
            }
        }
        finally {
            this.#syncingSurface = false;
        }
    }
    handleTextUpdate = (event) => {
        const update = event;
        if (this.#composing) {
            this.emit({ type: "updateComposition", text: update.text });
            return;
        }
        this.emit({
            type: "replace",
            start: update.updateRangeStart,
            end: update.updateRangeEnd,
            text: update.text,
        });
        const naturalCaret = update.updateRangeStart + update.text.length;
        if (update.selectionStart !== naturalCaret || update.selectionEnd !== naturalCaret) {
            this.emitSelection(update.selectionStart, update.selectionEnd);
        }
    };
    handleCompositionStart = () => {
        if (this.#target === undefined || this.#composing)
            return;
        this.#composing = true;
        this.emit({ type: "beginComposition" });
    };
    handleCompositionUpdate = (event) => {
        if (!this.#composing)
            return;
        this.emit({ type: "updateComposition", text: event.data ?? "" });
    };
    handleCompositionEnd = (event) => {
        if (!this.#composing)
            return;
        this.emit({ type: "commitComposition", text: event.data ?? undefined });
        this.#composing = false;
    };
    handleBeforeInput = (event) => {
        const input = event;
        if (this.#target === undefined || this.#composing || this.#target.readOnly)
            return;
        const command = beforeInputCommand(input, this.#target.multiline);
        if (command === "submit") {
            input.preventDefault();
            this.#onSubmit?.(this.#target.nodeId);
            return;
        }
        if (command === undefined)
            return;
        input.preventDefault();
        this.emit(command);
    };
    handleProxySelection = () => {
        if (this.#syncingSurface || this.#proxy === undefined || this.#target === undefined)
            return;
        const anchor = this.#proxy.selectionStart;
        const focus = this.#proxy.selectionEnd;
        if (anchor === this.#selection.anchor && focus === this.#selection.focus)
            return;
        this.emitSelection(anchor, focus);
    };
    handleCopy = (event) => {
        const clipboard = event.clipboardData;
        const target = this.#target;
        if (clipboard === null || target === undefined || target.password)
            return;
        event.preventDefault();
        const supplied = this.#onCopy?.();
        if (supplied === undefined) {
            clipboard.setData("text/plain", selectedText(this.#value, this.#selection));
            return;
        }
        // Markdown goes on `text/plain` so an editor that receives it keeps the
        // structure a plain-text paste would otherwise flatten.
        clipboard.setData("text/plain", supplied.markdown ?? supplied.text);
        if (supplied.html !== undefined)
            clipboard.setData("text/html", supplied.html);
    };
    handleCut = (event) => {
        this.handleCopy(event);
        const target = this.#target;
        if (target === undefined || target.readOnly || target.password)
            return;
        const [start, end] = orderedSelection(this.#selection);
        this.emit({ type: "replace", start, end, text: "" });
    };
    handlePaste = (event) => {
        const clipboard = event.clipboardData;
        const target = this.#target;
        if (clipboard === null || target === undefined || target.readOnly)
            return;
        event.preventDefault();
        const text = clipboard.getData("text/plain");
        const html = clipboard.getData("text/html");
        if (this.#onPaste?.({ html, text }) === true)
            return;
        this.emit({ type: "insert", text });
    };
    /** EditContext leaves navigation keys to the app; map them to Core moves. */
    handleKeyDown = (event) => {
        const key = event;
        const target = this.#target;
        if (target === undefined || this.#composing)
            return;
        // A key the Shell already answered is not ours to answer again: a document
        // editor consumes the arrows that drive its own menus, and moving the caret
        // as well would move it behind the reader's back.
        if (key.defaultPrevented)
            return;
        // No historyUndo/historyRedo beforeinput arrives on an EditContext host —
        // the browser undo stack is disabled there — so the shortcut itself is the
        // only signal. Core owns the actual history.
        if ((key.metaKey || key.ctrlKey) && !key.altKey) {
            const lowered = key.key.toLowerCase();
            if (lowered === "z" || lowered === "y") {
                if (key.cancelable)
                    key.preventDefault();
                this.emit({ type: lowered === "y" || key.shiftKey ? "redo" : "undo" });
                return;
            }
        }
        const word = key.ctrlKey || key.altKey;
        let direction;
        let granularity = "grapheme";
        switch (key.key) {
            case "ArrowLeft":
                direction = key.metaKey ? "lineStart" : "backward";
                granularity = word ? "word" : "grapheme";
                break;
            case "ArrowRight":
                direction = key.metaKey ? "lineEnd" : "forward";
                granularity = word ? "word" : "grapheme";
                break;
            case "ArrowUp":
                direction = "up";
                break;
            case "ArrowDown":
                direction = "down";
                break;
            case "Home":
                direction = "lineStart";
                break;
            case "End":
                direction = "lineEnd";
                break;
            default:
                return;
        }
        if (key.cancelable)
            key.preventDefault();
        try {
            this.#dispatch({
                type: "moveCaret",
                nodeId: target.nodeId,
                direction,
                granularity,
                extend: key.shiftKey,
            });
        }
        catch (cause) {
            this.#onError?.(toError(cause, "caret movement dispatch failed"));
        }
    };
    handleCharacterBoundsUpdate = (event) => {
        const request = event;
        this.#pendingCharacterBounds = [request.rangeStart, request.rangeEnd];
        if (this.fulfillCharacterBoundsRequest())
            return;
        const nodeId = this.#target?.nodeId;
        if (nodeId !== undefined) {
            this.#requestCharacterBounds?.(nodeId, request.rangeStart, request.rangeEnd);
        }
    };
    fulfillCharacterBoundsRequest() {
        const request = this.#pendingCharacterBounds;
        if (request === undefined || this.#editContext === undefined)
            return false;
        const bounds = this.#geometry?.characterBounds?.(request[0], request[1]);
        if (bounds === undefined || bounds.length !== request[1] - request[0])
            return false;
        this.#editContext.updateCharacterBounds(request[0], bounds);
        this.#pendingCharacterBounds = undefined;
        return true;
    }
    listen(target, type, listener) {
        const guarded = (event) => {
            try {
                listener(event);
            }
            catch (cause) {
                this.#onError?.(toError(cause, `native text input ${type} handler failed`));
            }
        };
        target.addEventListener(type, guarded);
        this.#removeListeners.push(() => target.removeEventListener(type, guarded));
    }
    assertUsable() {
        if (this.#disposed)
            throw new Error("native text input bridge is disposed");
    }
}
function beforeInputCommand(event, multiline) {
    switch (event.inputType) {
        case "insertText":
        case "insertReplacementText":
        case "insertFromDrop":
            return { type: "insert", text: event.data ?? "" };
        case "insertLineBreak":
        case "insertParagraph":
            return multiline ? { type: "insert", text: "\n" } : "submit";
        case "deleteContentBackward":
        case "deleteWordBackward":
            return { type: "deleteBackward" };
        case "deleteContentForward":
        case "deleteWordForward":
            return { type: "deleteForward" };
        case "historyUndo":
            return { type: "undo" };
        case "historyRedo":
            return { type: "redo" };
        default:
            return undefined;
    }
}
function createProxy(ownerDocument) {
    const proxy = ownerDocument.createElement("textarea");
    proxy.dataset.pingoInputProxy = "true";
    proxy.autocapitalize = "off";
    proxy.autocomplete = "off";
    proxy.spellcheck = false;
    Object.assign(proxy.style, {
        height: "1px",
        left: "-10000px",
        opacity: "0",
        position: "fixed",
        top: "0",
        width: "1px",
    });
    ownerDocument.body.append(proxy);
    return proxy;
}
function validateTarget(target) {
    if (!Number.isInteger(target.nodeId) || target.nodeId < 0 || target.nodeId > 0xffff_ffff) {
        throw new RangeError("editing target nodeId must be a u32");
    }
    if (target.revision < 0n)
        throw new RangeError("editing target revision must be non-negative");
    for (const offset of [target.selection.anchor, target.selection.focus]) {
        if (!Number.isInteger(offset) || offset < 0 || offset > target.value.length) {
            throw new RangeError("editing target selection is outside its UTF-16 value");
        }
        assertUtf16Boundary(target.value, offset, "editing target selection");
    }
}
function applyUtf16Replacement(value, start, end, replacement) {
    if (start < 0 || start > end || end > value.length)
        throw new RangeError("edit delta is invalid");
    assertUtf16Boundary(value, start, "edit delta start");
    assertUtf16Boundary(value, end, "edit delta end");
    return value.slice(0, start) + replacement + value.slice(end);
}
function assertUtf16Boundary(value, offset, label) {
    if (offset > 0 &&
        offset < value.length &&
        isHighSurrogate(value.charCodeAt(offset - 1)) &&
        isLowSurrogate(value.charCodeAt(offset))) {
        throw new RangeError(`${label} splits a surrogate pair`);
    }
}
function isHighSurrogate(codeUnit) {
    return codeUnit >= 0xd800 && codeUnit <= 0xdbff;
}
function isLowSurrogate(codeUnit) {
    return codeUnit >= 0xdc00 && codeUnit <= 0xdfff;
}
function orderedSelection(selection) {
    return selection.anchor <= selection.focus
        ? [selection.anchor, selection.focus]
        : [selection.focus, selection.anchor];
}
function selectedText(value, selection) {
    const [start, end] = orderedSelection(selection);
    return value.slice(start, end);
}
function toError(cause, message) {
    return cause instanceof Error ? cause : new Error(message, { cause });
}
//# sourceMappingURL=native-input.js.map